// What opens from the learner's Capability tab:
//   - Analysis: Elliott's assessment of one Roleplay, with the capability's
//     four levels beneath and the one reached picked out. The levels are shown
//     here, after a Roleplay, and not before.
//   - A Roleplay the learner starts themselves on a capability: graded (it
//     counts toward their level) or practice (it never does).
//
// Practice follows the lesson Roleplay's rule: up to three attempts, Elliott
// feeding back and asking again while the response is short of Advanced. A
// graded Roleplay is a self-assessment: ONE attempt, as in an Assessment, then
// the level and Elliott's analysis. Elliott writes the situation from the
// capability's levels so the answer can be placed clearly in one of the four,
// and grades every attempt; the backend keeps the round's best attempt
// (POST /v3/member/capability/roleplays…).

import React, { useEffect, useRef, useState } from 'react';
import { Box, Dialog, Typography } from '@mui/material';
import { LEVEL_COLOR, LEVEL_NAME } from '../../Program/components/learning-lifecycle/CapabilityGapsList';
import {
  LEVEL_ORDER,
  type CapabilityLevel,
  type RoleplayResult
} from '../../Program/components/learning-lifecycle/teamCapability';
import type { LevelCriteria } from '../capabilitySetup';
import { apiMessage, capabilityApi, type AttemptView, type RecordedRoleplayView } from '../../../../../api/capability';
import {
  ActionButton,
  BODY,
  BRANDY,
  FONT,
  LABEL,
  LINE,
  SECTION_TITLE,
  TEXT,
  TEXT_2,
  TEXT_3,
  TEXTAREA_SX,
  WELL
} from '../designSystem';

/** A practice round (like a lesson Roleplay) allows this many attempts; a graded Roleplay allows one. */
export const PRACTICE_ATTEMPTS = 3;

const PAPER = {
  width: 640,
  maxWidth: '92vw',
  maxHeight: '88vh',
  bgcolor: '#1C1C1C',
  border: `1px solid ${LINE}`,
  borderRadius: '8px',
  backgroundImage: 'none',
  display: 'flex',
  flexDirection: 'column'
};

/** The capability's four levels, the one reached picked out. */
const LevelsTable: React.FC<{ criteria: Partial<Record<CapabilityLevel, string>>; reached?: CapabilityLevel }> = ({
  criteria,
  reached
}) => (
  <Box sx={{ border: `1px solid ${LINE}`, borderRadius: '4px', bgcolor: WELL }}>
    {LEVEL_ORDER.map((level, i) => {
      const on = level === reached;
      return (
        <Box
          key={level}
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            ...(i > 0 ? { borderTop: '1px solid #1F1F1F' } : {}),
            bgcolor: on ? `${LEVEL_COLOR[level]}14` : 'transparent'
          }}
        >
          <Box
            sx={{
              width: 112,
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              px: '12px',
              borderRight: `1px solid ${LINE}`
            }}
          >
            <Typography sx={{ ...LABEL, color: LEVEL_COLOR[level] }}>{LEVEL_NAME[level]}</Typography>
          </Box>
          <Typography
            sx={{ ...BODY, flex: 1, fontSize: 12.5, lineHeight: 1.5, px: '12px', py: '8px', color: on ? TEXT : TEXT_2 }}
          >
            {criteria[level] || 'Not set'}
          </Typography>
        </Box>
      );
    })}
  </Box>
);

const Heading: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ ...LABEL, color: BRANDY, display: 'flex', alignItems: 'center', gap: '6px' }}>
    {children}
  </Typography>
);

/** Elliott's assessment of one Roleplay, and the levels it was graded against. */
export const AnalysisDialog: React.FC<{
  capabilityName: string;
  roleplay: RoleplayResult | null;
  /** The capability's levels, where the Roleplay itself did not record them. */
  criteria: LevelCriteria;
  onClose: () => void;
}> = ({ capabilityName, roleplay, criteria, onClose }) => (
  <Dialog open={Boolean(roleplay)} onClose={onClose} maxWidth={false} PaperProps={{ sx: PAPER }}>
    {roleplay && (
      <>
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            p: '20px',
            display: 'flex',
            flexDirection: 'column',
            gap: '14px'
          }}
        >
          <Typography sx={{ ...SECTION_TITLE, whiteSpace: 'normal' }}>
            {capabilityName}
            <Box component="span" sx={{ color: '#565656', mx: '10px' }}>
              |
            </Box>
            <Box component="span" sx={{ color: LEVEL_COLOR[roleplay.level] }}>
              {LEVEL_NAME[roleplay.level]}
            </Box>
          </Typography>
          <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_3, mt: '-8px' }}>{roleplay.source}</Typography>
          <Heading>Elliott&apos;s Analysis</Heading>
          <Typography sx={{ ...BODY, color: roleplay.analysis ? TEXT : TEXT_3 }}>
            {roleplay.analysis || "Elliott's analysis isn't available for this Roleplay."}
          </Typography>
          <Heading>What each level looks like</Heading>
          <LevelsTable criteria={roleplay.criteria ?? criteria} reached={roleplay.level} />
        </Box>
        <Box
          sx={{
            flexShrink: 0,
            display: 'flex',
            justifyContent: 'flex-end',
            p: '12px 20px',
            borderTop: `1px solid ${LINE}`
          }}
        >
          <ActionButton primary onClick={onClose}>
            Done
          </ActionButton>
        </Box>
      </>
    )}
  </Dialog>
);

const Waiting: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography role="status" sx={{ ...BODY, fontSize: 12.5, color: TEXT_3, fontStyle: 'italic' }}>
    {children}
  </Typography>
);

/**
 * A Roleplay the learner starts on one capability. Practice: up to three
 * attempts, Elliott saying why and asking again while short of Advanced, the
 * best one kept. Graded: one attempt, then the level it was placed at.
 */
export const RoleplayDialog: React.FC<{
  open: boolean;
  kind: 'graded' | 'practice';
  capabilityId: string;
  capabilityName: string;
  criteria: LevelCriteria;
  onClose: () => void;
  /** The Roleplay as recorded, once it is finished. */
  onFinished: (roleplay: RecordedRoleplayView) => void;
}> = ({ open, kind, capabilityId, capabilityName, criteria, onClose, onFinished }) => {
  const [session, setSession] = useState<{ id: string; situation: string; allowed: number } | null>(null);
  const [attempts, setAttempts] = useState<AttemptView[]>([]);
  const [responses, setResponses] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [finished, setFinished] = useState<RecordedRoleplayView | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);

  // Elliott writes the situation when the dialog opens.
  useEffect(() => {
    if (!open || started.current) return;
    started.current = true;
    capabilityApi
      .startRoleplay({ capabilityId, kind })
      .then((s) => setSession({ id: s.sessionId, situation: s.situation, allowed: s.attemptsAllowed }))
      .catch((err) => setError(apiMessage(err, 'Elliott could not start this Roleplay. Try again in a moment.')));
  }, [open, capabilityId, kind]);

  const send = async () => {
    const response = draft.trim();
    if (!response || !session || sending) return;
    setSending(true);
    setError(null);
    try {
      const attempt = await capabilityApi.answerRoleplay(session.id, response);
      setAttempts((prev) => [...prev, attempt]);
      setResponses((prev) => [...prev, response]);
      setDraft('');
      if (attempt.finished && attempt.recorded) {
        setFinished(attempt.recorded);
        onFinished(attempt.recorded);
      }
    } catch (err) {
      setError(apiMessage(err, 'Elliott could not read your answer. Send it again.'));
    } finally {
      setSending(false);
    }
  };

  const graded = kind === 'graded';
  const allowed = session?.allowed ?? (graded ? 1 : PRACTICE_ATTEMPTS);
  return (
    <Dialog open={open} onClose={onClose} maxWidth={false} PaperProps={{ sx: PAPER }}>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          p: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '14px',
          fontFamily: FONT
        }}
      >
        <Typography sx={{ ...SECTION_TITLE, whiteSpace: 'normal' }}>
          {graded ? 'Scored Roleplay' : 'Practice Roleplay'}
          <Box component="span" sx={{ color: '#565656', mx: '10px' }}>
            |
          </Box>
          <Box component="span" sx={{ color: TEXT_3, fontWeight: 400 }}>
            {capabilityName}
          </Box>
        </Typography>
        <Typography sx={{ ...BODY, fontSize: 12, color: TEXT_3, mt: '-8px' }}>
          {graded
            ? 'Elliott scores this Roleplay, and the result counts toward your score.'
            : `Not graded, and never counts toward your score. You can practice up to ${PRACTICE_ATTEMPTS} times, with feedback after each.`}
        </Typography>

        <Heading>Elliott</Heading>
        {session ? (
          <Typography sx={{ ...BODY, color: TEXT, whiteSpace: 'pre-wrap' }}>{session.situation}</Typography>
        ) : (
          !error && <Waiting>Elliott is writing a situation for you...</Waiting>
        )}

        {attempts.map((a, i) => (
          <Box
            key={a.attemptNumber}
            sx={{ display: 'flex', flexDirection: 'column', gap: '8px', pt: '12px', borderTop: `1px solid ${LINE}` }}
          >
            <Typography sx={LABEL}>
              {allowed > 1 ? `Your answer ${a.attemptNumber}` : 'Your answer'}
            </Typography>
            <Typography sx={{ ...BODY, color: TEXT_2, whiteSpace: 'pre-wrap' }}>{responses[i]}</Typography>
            <Heading>Elliott&apos;s Feedback</Heading>
            <Typography sx={{ ...BODY, color: TEXT }}>{a.feedback}</Typography>
          </Box>
        ))}

        {error && (
          <Typography role="alert" sx={{ ...BODY, fontSize: 12.5, color: '#FF6A55' }}>
            {error}
          </Typography>
        )}

        {finished ? (
          <>
            <Heading>{graded ? 'Your Score On This Roleplay' : 'How You Did'}</Heading>
            <LevelsTable criteria={finished.criteria || criteria} reached={finished.level} />
          </>
        ) : (
          session && (
            <>
              {attempts.length > 0 && (
                <Typography sx={{ ...BODY, fontSize: 12.5, color: BRANDY }}>
                  Try again with that in mind. You can practice {allowed - attempts.length} more{' '}
                  {allowed - attempts.length === 1 ? 'time' : 'times'}.
                </Typography>
              )}
              {graded && (
                <Typography sx={{ ...BODY, fontSize: 12.5, color: BRANDY, fontWeight: 600 }}>
                  Take your time. You answer each Roleplay once, so send your answer when you are happy with it.
                </Typography>
              )}
              {sending ? (
                <Waiting>Elliott is reading your answer...</Waiting>
              ) : (
                <Box
                  component="textarea"
                  autoFocus
                  rows={5}
                  value={draft}
                  aria-label="Your response"
                  placeholder="Respond as you would in the moment"
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
                  sx={TEXTAREA_SX}
                />
              )}
            </>
          )
        )}
      </Box>
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '8px',
          p: '12px 20px',
          borderTop: `1px solid ${LINE}`
        }}
      >
        <ActionButton onClick={onClose}>{finished ? 'Done' : 'Cancel'}</ActionButton>
        {!finished && session && (
          <ActionButton primary disabled={!draft.trim() || sending} onClick={send}>
            Send
          </ActionButton>
        )}
      </Box>
    </Dialog>
  );
};
